import type { CreditView } from "./display";

/**
 * The single source of truth for what a credit surface says while a build is
 * RUNNING and part of the balance is reserved rather than spent.
 *
 * The bug this exists for: mid-build, the builder's AI-balance panel showed
 *
 *     Today        50/50
 *     Total pool  250/300
 *     50 on hold while your build runs — unused credits return when it finishes.
 *
 * Every number was correct and the sentence was true, yet together they read as
 * a contradiction — 50 of 50 still available, and 50 simultaneously "on hold" —
 * because nothing on screen said WHICH balance the hold came out of. It came out
 * of the pool (reserve_credits deducts it immediately, so 250 is already net of
 * it) and NOT out of today's allowance (/api/credits subtracts unsettled holds
 * back out of `daily_used`, so today still reads 0 spent). Both halves of that
 * are load-bearing, and neither was said.
 *
 * So the copy here always answers four questions in order, in the same words on
 * every surface:
 *
 *   1. what can I spend right now?            → availableNow
 *   2. how much is temporarily reserved?      → held
 *   3. where did the reservation come from?   → the pool, not today's allowance
 *   4. what happens when the build finishes?  → the unused part comes back
 *
 * Plan-agnostic on purpose: free, trial and paid accounts all reserve credits
 * the same way, so the wording never names a plan — only the daily-allowance
 * clause varies, and only because uncapped accounts have no daily allowance to
 * talk about.
 */
export interface CreditHoldCopy {
  /** Credits reserved by the in-flight build. Always > 0 (null is returned otherwise). */
  held: number;
  /**
   * Spendable right now, already net of the hold — `view.availableToday`, which
   * is the daily room for a capped account and the pool balance otherwise. This
   * is the number the acceptance criteria call "available to spend right now".
   */
  availableNow: number;
  /** Banked pool balance, already net of the hold. */
  poolRemaining: number;
  /** Per-day cap, or null for accounts with no daily window. */
  dailyLimit: number | null;
  /** True when a daily allowance exists and therefore needs explaining. */
  isDailyCapped: boolean;
  /** Row label for the reservation: "On hold for this build". */
  heldLabel: string;
  /** The reservation as a grouped number: "50". */
  heldValue: string;
  /** Chip/inline length: "50 on hold". */
  short: string;
  /**
   * The explanation, 2–3 plain sentences. Self-contained: it repeats the held
   * count, so it reads correctly on its own in a banner as well as underneath a
   * labelled row in the balance panel.
   */
  detail: string;
  /**
   * One sentence for a banner that has already quoted the balance — the chat
   * low-credit and out-of-credits notices. Says "another N", never "N of those":
   * the balance those banners quote is already net of the hold, so framing the
   * held credits as a subset of it would double-count them in the reader's head.
   */
  banner: string;
  /** Everything on one line, for `title=` / `aria-label` on compact surfaces. */
  summary: string;
}

const fmt = (n: number) => n.toLocaleString();

/**
 * Hold copy for `view`, or null when nothing is reserved — so a caller can write
 * `const hold = formatCreditHold(view)` and treat it as the whole "is a build
 * holding credits right now?" question.
 */
export function formatCreditHold(view: CreditView): CreditHoldCopy | null {
  const held = Math.max(0, Math.round(view.onHold));
  if (held <= 0) return null;

  const availableNow = Math.max(0, view.availableToday);
  const poolRemaining = Math.max(0, view.totalRemaining);
  const isDailyCapped = view.isFreeDailyCapped && view.dailyLimit != null;
  const dailyLimit = isDailyCapped ? view.dailyLimit : null;

  const them = held === 1 ? "it" : "them";
  const verb = held === 1 ? "is" : "are";
  const noun = held === 1 ? "credit" : "credits";

  // Sentence 1 — the reservation itself, and the word that does the most work
  // on this whole surface: reserved, NOT spent.
  const sentences = [
    `${fmt(held)} ${noun} ${verb} held for the build running now — reserved, not spent.`,
  ];

  // Sentence 2 — which balance it came out of. Skipped when the pool reads 0,
  // where "your balance of 0 already has them taken out" is technically true and
  // completely unhelpful; the daily half is still worth saying on its own.
  if (poolRemaining > 0 && dailyLimit != null) {
    sentences.push(
      `Your balance of ${fmt(poolRemaining)} already has ${them} taken out, and today's ${fmt(dailyLimit)}-credit allowance doesn't count ${them} yet.`,
    );
  } else if (poolRemaining > 0) {
    sentences.push(`Your balance of ${fmt(poolRemaining)} already has ${them} taken out.`);
  } else if (dailyLimit != null) {
    sentences.push(`Today's ${fmt(dailyLimit)}-credit allowance doesn't count ${them} yet.`);
  }

  // Sentence 3 — the return promise, and (for capped accounts) the fact that the
  // daily number moves only at settlement. That second clause is what makes a
  // full "Today 50/50" beside a 50-credit hold stop looking like a lie.
  sentences.push(
    dailyLimit != null
      ? `When the build finishes, whatever it didn't use goes back to your balance, and today's allowance drops by what the build actually cost.`
      : `When the build finishes, whatever it didn't use goes back to your balance.`,
  );

  return {
    held,
    availableNow,
    poolRemaining,
    dailyLimit,
    isDailyCapped,
    heldLabel: "On hold for this build",
    heldValue: fmt(held),
    short: `${fmt(held)} on hold`,
    detail: sentences.join(" "),
    banner:
      `Another ${fmt(held)} ${noun} ${verb} on hold for the build running now — ` +
      `whatever it doesn't use comes back when it finishes.`,
    summary:
      `${fmt(availableNow)} available to spend now · ${fmt(held)} on hold for the build running now — ` +
      `unused credits come back when it finishes.`,
  };
}
